import { useEffect, useMemo, useRef, useState } from "react";
import { X, Copy, Check } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { setAdvancedMatching } from "@/lib/metaPixel";
import { shopifyImg } from "@/lib/shopifyImage";

const SESSION_KEY = "ccp_seen";
const SPUN_KEY = "ccp_spun";
const emailSchema = z.string().trim().email("Enter a valid email").max(255);

type Step = "spin" | "email" | "reveal";

interface PromoCaptchaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// 6 wheel slices (2 of each). Colors alternate so B2G1F sits in slices 0 & 3.
const SLICES = [
  { label: "BUY 2 GET 1 FREE", short: "B2G1", color: "#FA5021", text: "#F5EFE2", isPrize: true },
  { label: "$10 OFF", short: "$10 OFF", color: "#C6A971", text: "#2A1510", isPrize: false },
  { label: "FREE SHIPPING", short: "FREE SHIP", color: "#F5EFE2", text: "#2A1510", isPrize: false },
  { label: "BUY 2 GET 1 FREE", short: "B2G1", color: "#FA5021", text: "#F5EFE2", isPrize: true },
  { label: "$10 OFF", short: "$10 OFF", color: "#C6A971", text: "#2A1510", isPrize: false },
  { label: "FREE SHIPPING", short: "FREE SHIP", color: "#F5EFE2", text: "#2A1510", isPrize: false },
];
const SLICE_ANGLE = 360 / SLICES.length;
const PRIZE_INDICES = [0, 3];

// Flattened hat lifestyle photos (from LifestyleGallery).
const SLIDESHOW_IMAGES = [
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-8698.png?v=1721837076",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/72E4CC42-6C48-4166-8830-0F514EDAE03F.jpg?v=1722660089",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/0B99ADD6-0420-4705-AADE-6DB162E89D02.png?v=1765165220",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-6172.png?v=1781401862",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Shopify_Slideshow_Images_-_2.png?v=1765204700",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-8691.png?v=1721837281",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-8704.png?v=1721836813",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG_9396.jpg?v=1706758562",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Your_paragraph_text_8586bac6-1a53-40e4-854d-421df536a11f.png?v=1705362977",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Your_paragraph_text_51fe2feb-1f5f-44d4-b318-c101426b8565.png?v=1703388095",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG_9359.jpg?v=1703359618",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/A1A4F653-F544-4BEA-82A4-2B3EEA8C419F.jpg?v=1782921824",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/F51048CF-B6A1-4FDC-9814-196DE253600C.jpg?v=1722659970",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG_6277.jpg?v=1700719423",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/90287317-8853-450A-A3EF-65E054DCB88B.jpg?v=1782921822",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-8696.png?v=1721837343",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/91E8A279-FCA2-4CDC-9EDB-5AA7C6876D41.png?v=1765165220",
  "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-6175.png?v=1781401861",
];

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function slicePath(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const start = polarToCartesian(cx, cy, r, endDeg);
  const end = polarToCartesian(cx, cy, r, startDeg);
  const largeArc = endDeg - startDeg <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

function Slideshow() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const id = setInterval(() => {
      setActive((a) => (a + 1) % SLIDESHOW_IMAGES.length);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const nextIdx = (active + 1) % SLIDESHOW_IMAGES.length;

  return (
    <div className="relative hidden h-full w-full overflow-hidden bg-[#412019] sm:block">
      {SLIDESHOW_IMAGES.map((src, i) => {
        if (i !== active && i !== nextIdx) return null;
        return (
          <img
            key={`${src}-${i}`}
            src={shopifyImg(src, { width: 700, format: "webp" })}
            alt=""
            aria-hidden
            draggable={false}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-in-out ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
            loading="eager"
            decoding="async"
          />
        );
      })}
    </div>
  );
}

export function PromoCaptchaModal({ open, onOpenChange }: PromoCaptchaModalProps) {
  const { data: settings } = useSiteSettings();
  const code = settings?.promo_captcha_code ?? "BUY2GET1";

  const [step, setStep] = useState<Step>("spin");
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      let hasSpun = false;
      try { hasSpun = sessionStorage.getItem(SPUN_KEY) === "1"; } catch { /* sessionStorage unavailable */ }
      setStep(hasSpun ? "email" : "spin");
      setRotation(0);
      setSpinning(false);
      setEmail("");
      setSubmitting(false);
      setErrorMsg(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function markSeen() {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* sessionStorage unavailable */ }
  }

  function markSpun() {
    try { sessionStorage.setItem(SPUN_KEY, "1"); } catch { /* sessionStorage unavailable */ }
  }

  function handleClose() {
    // Only mark seen once the user has claimed (reveal step). Otherwise the
    // trigger can reopen the modal and land them on the email step.
    if (step === "reveal") markSeen();
    onOpenChange(false);
  }

  function handleSpin() {
    if (spinning) return;
    setSpinning(true);
    // Always land on a B2G1F slice; pick between the two, jitter within slice.
    const targetSlice = PRIZE_INDICES[Math.floor(Math.random() * PRIZE_INDICES.length)];
    const sliceCenter = targetSlice * SLICE_ANGLE + SLICE_ANGLE / 2;
    const jitter = (Math.random() - 0.5) * (SLICE_ANGLE - 10);
    const spins = 6; // full rotations
    // Pointer sits at the top (0deg). Wheel rotates CW; we want (currentRot + delta) % 360
    // to leave the target slice under the pointer -> rotation ≡ -sliceCenter - jitter (mod 360)
    const current = rotation % 360;
    const desired = (360 - sliceCenter - jitter) % 360;
    let delta = desired - current;
    if (delta < 0) delta += 360;
    const finalRotation = rotation + spins * 360 + delta;
    setRotation(finalRotation);
    setTimeout(() => {
      setSpinning(false);
      markSpun();
      setTimeout(() => setStep("email"), 500);
    }, 4200);
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setErrorMsg(parsed.error.issues[0]?.message ?? "Invalid email");
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const { error } = await supabase
        .from("promo_signups")
        .insert({ email: parsed.data, source: "spin_wheel_popup" });
      if (error && error.code !== "23505") throw error;
      setAdvancedMatching({ em: parsed.data });
      setStep("reveal");
      markSeen();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  }

  const wheelSvg = useMemo(() => {
    const cx = 100, cy = 100, r = 98;
    return (
      <svg viewBox="0 0 200 200" className="h-full w-full">
        {SLICES.map((s, i) => {
          const start = i * SLICE_ANGLE;
          const end = start + SLICE_ANGLE;
          const mid = start + SLICE_ANGLE / 2;
          const labelPos = polarToCartesian(cx, cy, r * 0.62, mid);
          return (
            <g key={i}>
              <path d={slicePath(cx, cy, r, start, end)} fill={s.color} stroke="#2A1510" strokeWidth="1.5" />
              <text
                x={labelPos.x}
                y={labelPos.y}
                fill={s.text}
                fontSize="10"
                fontWeight="800"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${mid} ${labelPos.x} ${labelPos.y})`}
                style={{ fontFamily: "var(--font-ui, system-ui)", letterSpacing: "0.05em" }}
              >
                {s.short}
              </text>
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#2A1510" strokeWidth="3" />
      </svg>
    );
  }, []);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="promo-wheel-title"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/70 px-2 py-2 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="relative my-auto flex max-h-[calc(100dvh-1rem)] w-full max-w-4xl flex-col overflow-hidden shadow-2xl sm:flex-row"
        style={{
          background: "#F5EFE2",
          borderRadius: "4px",
        }}
      >
        <button
          ref={closeBtnRef}
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full bg-[#2A1510]/80 text-[#F5EFE2] transition hover:bg-[#2A1510]"
        >
          <X className="h-4 w-4" />
        </button>

        {/* LEFT — wheel / email / reveal */}
        <div className="flex w-full flex-col items-center justify-center px-5 py-6 sm:w-1/2 sm:px-8 sm:py-10">
          {step === "spin" && (
            <>
              <p className="font-ui text-[10px] font-bold uppercase tracking-[0.3em] text-[#C6A971]">
                Spin to win
              </p>
              <h2 id="promo-wheel-title" className="mt-2 text-center font-heading text-2xl font-black uppercase leading-tight text-[#2A1510] sm:text-3xl">
                Your prize awaits
              </h2>

              <div className="relative mt-5 aspect-square w-full max-w-[300px]">
                {/* Pointer */}
                <div
                  className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1"
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: "12px solid transparent",
                    borderRight: "12px solid transparent",
                    borderTop: "20px solid #2A1510",
                    filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.3))",
                  }}
                />
                {/* Wheel */}
                <div
                  className="h-full w-full rounded-full"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transition: spinning ? "transform 4s cubic-bezier(0.17, 0.67, 0.24, 1)" : "none",
                    filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.25))",
                  }}
                >
                  {wheelSvg}
                </div>
                {/* Hub */}
                <div className="pointer-events-none absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-[#2A1510] bg-[#F5EFE2]" />
              </div>

              <button
                type="button"
                onClick={handleSpin}
                disabled={spinning}
                className="btn-cc-popup mt-6 w-full max-w-[280px]"
              >
                <span>{spinning ? "Spinning…" : "Spin the wheel"}</span>
              </button>
              <p className="mt-3 text-center font-ui text-[10px] uppercase tracking-widest text-[#2A1510]/70">
                One spin per visitor
              </p>
            </>
          )}

          {step === "email" && (
            <div className="w-full text-center">
              <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full border-2 border-[#C6A971] bg-[#C6A971]/10 text-[#2A1510]">
                <Check className="h-7 w-7" strokeWidth={3} />
              </div>
              <p className="font-ui text-[10px] font-bold uppercase tracking-[0.3em] text-[#C6A971]">You won</p>
              <h2 className="mt-2 font-heading text-2xl font-black uppercase text-[#2A1510] sm:text-3xl">
                Buy 2 Hats, Get 1 Free
              </h2>
              <p className="mt-2 font-ui text-sm normal-case text-[#2A1510]">
                Drop your email to unlock your code.
              </p>
              <form onSubmit={handleEmailSubmit} className="mt-5 space-y-3 text-left">
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => { setEmail(e.target.value.toLowerCase()); setErrorMsg(null); }}
                  placeholder="you@example.com"
                  maxLength={255}
                  className="w-full border-2 border-[#D4CCBC] bg-[#FDFAF3] px-4 py-3 font-ui text-sm lowercase text-[#2A1510] placeholder:text-[#9C9287] focus:border-[#FA5021] focus:outline-none"
                />
                {errorMsg && (
                  <p className="font-ui text-xs font-semibold text-red-700">{errorMsg}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-cc-popup w-full"
                >
                  <span>{submitting ? "Saving…" : "Claim my prize"}</span>
                </button>
                <p className="text-center font-ui text-[10px] uppercase text-[#2A1510]/70">
                  No spam. Unsubscribe anytime.
                </p>
              </form>
            </div>
          )}

          {step === "reveal" && (
            <div className="w-full text-center">
              <div className="mx-auto mb-3 inline-block border-2 border-[#2A1510] px-4 py-1 font-ui text-[10px] uppercase text-[#2A1510]">
                Your Code
              </div>
              <div
                className="mx-auto mb-4 inline-block bg-[#F5EFE2] px-6 py-4 font-heading text-3xl font-black uppercase text-[#2A1510]"
                style={{ border: "2px solid #2A1510", borderRadius: "2px", boxShadow: "4px 4px 0 0 #C6A971" }}
              >
                {code}
              </div>
              <div>
                <button
                  type="button"
                  onClick={copyCode}
                  aria-label="Copy code"
                  className="mx-auto mb-5 inline-flex items-center gap-2 border-2 border-[#2A1510] bg-[#2A1510] px-5 py-2 font-ui text-xs font-bold uppercase text-[#F5EFE2] transition hover:bg-[#412019]"
                  style={{ borderRadius: "2px" }}
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy code
                </button>
              </div>
              <h2 className="font-heading text-xl font-black uppercase text-[#2A1510]">
                Buy 2 Hats, Get 1 Free
              </h2>
              <p className="mt-1 font-ui text-xs normal-case text-[#2A1510]">
                Use at checkout. One code per order.
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="btn-cc-popup mt-5 w-full"
              >
                <span>Shop hats</span>
              </button>
            </div>
          )}
        </div>

        {/* RIGHT — image slideshow */}
        <div className="hidden sm:block sm:w-1/2">
          <Slideshow />
        </div>
      </div>
    </div>
  );
}
